import type { ComponentType, FC } from "react";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { useTranslation } from "@privacy/translations";
import FormControl from "@mui/material/FormControl";
import { useFormContext } from "react-hook-form";
import { InputAdornment } from "@mui/material";
import type { IconProps } from "../../../shared/icons";
import type { InputProps } from "../../DynamicInput";
import { TextActionButtonsSingleLine, useTextInput } from "../../inlineForm/inputs/Text";
import { TextFieldActionButtons } from "../../../shared/TextFieldActionButtons";
import { ErrorMessage } from "../../inlineForm/ErrorMessage";
import { useDynamicFormState } from "../../DynamicFormProvider";
import { isAnswerRequestEmpty } from "../../shared/isAnswerRequestEmpty";

type TextProps = InputProps<string> & {
  Icon?: ComponentType<IconProps>;
  numberOfRows?: number;
};

export const Text: FC<TextProps> = ({ questionCard, readonly, numberOfRows = 1, Icon, onSave }) => {
  const { t } = useTranslation();
  const { navigationBlockState, mandatoryQuestionErrorMessage } = useDynamicFormState();
  const form = useFormContext();
  const {
    handleCancel,
    handleSave,
    isDirty,
    isSaving,
    isSaveError,
    field,
    inputState,
    inputRef,
    setIsEditing,
    handleKeyDown,
  } = useTextInput({ id: questionCard.id, onSave, form, numberOfRows, isReadOnly: readonly });

  return (
    <Stack sx={{ width: "100%" }}>
      <Stack
        onBlur={TextFieldActionButtons.onTextFieldWrapperBlur({ isDirty, onCancel: handleCancel })}
        sx={{ position: "relative" }}
      >
        <FormControl>
          <TextField
            {...field}
            value={field.value ?? ""}
            inputRef={inputRef}
            onKeyDown={handleKeyDown}
            size="small"
            multiline={numberOfRows > 1}
            minRows={numberOfRows}
            maxRows={inputState === "editing" ? undefined : numberOfRows}
            onFocus={() => setIsEditing(true)}
            fullWidth
            disabled={readonly}
            sx={numberOfRows > 1 ? TextFieldActionButtons.TextFieldSx : undefined}
            slotProps={{
              input: {
                startAdornment: Icon && <InputAdornment position="start">{<Icon size={16} />}</InputAdornment>,
                endAdornment: numberOfRows > 1 && (
                  <TextFieldActionButtons
                    isEditing={inputState === "editing"}
                    isSaving={isSaving}
                    onSave={handleSave}
                    isDirty={isDirty}
                    onCancel={handleCancel}
                  />
                ),
              },
            }}
          />

          {numberOfRows === 1 && (
            <TextActionButtonsSingleLine
              isEditing={inputState === "editing"}
              isSaving={isSaving}
              onSave={handleSave}
              isDirty={isDirty}
              onCancel={handleCancel}
            />
          )}
        </FormControl>
      </Stack>
      {!isSaving && isSaveError && <ErrorMessage />}
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
      {(navigationBlockState === "cancelled" || navigationBlockState === "blocked") && isDirty && (
        <ErrorMessage variant="warning" errorMessage={t("DynamicForm.TextInput.UnsavedChangesWarning")} />
      )}
    </Stack>
  );
};
